import { MaskedTextbox, SplitTextbox } from '@policygenius/mortar';
import React, { useState } from 'react';
import { useField } from 'react-final-form';

import useEffectAfterMount from 'hooks/useEffectAfterMount';

import { HeightFieldProps, HeightState } from './types';
import { feetToInches, inchesToFeet } from './utils';

const HeightField: React.FC<HeightFieldProps> = ({ label, name, config }) => {
  const { input, meta } = useField(name, config);
  const [{ feetValue, inchesValue }, setFeetAndInches] = useState<HeightState>(() => {
    const heightInInches = meta.initial;
    const { feet, inches } = inchesToFeet(heightInInches as number);

    return {
      feetValue: heightInInches ? feet : '',
      inchesValue: heightInInches ? inches : '',
    };
  });

  useEffectAfterMount(() => {
    if (feetValue === '' || inchesValue === '') {
      input.onChange(null);
    } else {
      input.onChange(feetToInches(feetValue, inchesValue));
    }
  }, [feetValue, inchesValue]);

  const onFeetChange: MaskedTextbox.Props['onChange'] = ({ unmasked }) =>
    setFeetAndInches((curr) => ({
      ...curr,
      feetValue: unmasked ? Number(unmasked) : '',
    }));

  const onInchesChange: MaskedTextbox.Props['onChange'] = ({ unmasked }) =>
    setFeetAndInches((curr) => ({
      ...curr,
      inchesValue: unmasked ? Number(unmasked) : '',
    }));

  const defaults = meta.initial ? inchesToFeet(meta.initial) : undefined;
  const errorMessage = meta.touched && !meta.active ? meta.error : undefined;

  return (
    <SplitTextbox id={name} errorMessage={errorMessage} label={label}>
      <MaskedTextbox
        {...input}
        id="ft"
        type="text"
        name={`${name}Feet`}
        label="Feet"
        defaultValue={defaults?.feet}
        maskOptions={{
          mask: Number,
          validate: (value: string) => !value || (Number(value) >= 4 && Number(value) < 9),
        }}
        onChange={onFeetChange}
        suffix="ft"
        inputMode="numeric"
      />
      <MaskedTextbox
        {...input}
        id="in"
        type="text"
        name={`${input.name}Inches`}
        label="Inches"
        defaultValue={defaults?.inches}
        maskOptions={{
          mask: Number,
          validate: (value: string) => !value || (Number(value) >= 0 && Number(value) < 12),
        }}
        onChange={onInchesChange}
        suffix="in"
        inputMode="numeric"
      />
    </SplitTextbox>
  );
};

export default HeightField;
